import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { FixNumber } from "../../../../common/orders";
import { getCurrencyShorthand } from "../../../../providers/StationProvider";
import SimpleTable from "../../../common/tables/SimpleTable";
import { ToggleSurface } from "../../../common/core/Surface";
import { Button, Grid, Typography } from "@mui/material";
import { MissionEnums } from "../../../../common/constants";
import { AllTasksCompleted, AssumeStorageState } from "../../../../common/mission";
import { useInvoiceExport } from "../../../../hooks/useInvoiceExport";
import MissingExportSettingsWarning from "../../../common/billing/MissingExportSettingsWarning";
import InvoiceExportDialog from "../../../common/billing/dialogs/InvoiceExportDialog";
import { TimeView } from "../../../common/core/Time";

export default function ReadyForInvoicingSection() {
  const { t } = useTranslation();
  const { tabId } = useParams();
  const navigate = useNavigate();

  const { orders, loading, exportAction, dialogAction, exportResult, exportAll, hasExportSettings, working } = useInvoiceExport();
  const [selectionModel, setSelectionModel] = useState([]);
  const showLoading = loading;

  const columns = useMemo(() => {
    return [
      {
        field: "number",
        type: "number",
        headerName: t("entity:order:number"),
        flex: 0
      },
      {
        field: "missionNumber",
        type: "number",
        headerName: t("readyForInvoicing:fieldMission"),
        flex: 0
      },
      {
        field: "storage",
        headerName: t("readyForInvoicing:fieldStorageState"),
        valueGetter: (_, row) => AssumeStorageState(row.mission),
        valueFormatter: (value) =>
          value === MissionEnums.StorageState.None ? t("readyForInvoicing:noStorage") :
            value === MissionEnums.StorageState.Stored ?
              t("readyForInvoicing:storageNotCompleted") :
              t("readyForInvoicing:storageCompleted"),
      },
      {
        field: "taskState",
        type: "boolean",
        headerName: t("readyForInvoicing:fieldTaskState"),
        valueGetter: (_, row) => AllTasksCompleted(row.mission),
        renderCell: (params) => params.value ? t("readyForInvoicing:tasksCompleted") : t("readyForInvoicing:tasksNotCompleted"),
        flex: 1,
        minWidth: 120
      },
      {
        field: "missionCreated",
        type: "date",
        headerName: t("readyForInvoicing:fieldMissionCreated"),
        valueGetter: (_, row) => new Date(row.mission?.created),
        renderCell: (params) => <TimeView time={params.row.mission?.created} />,
        flex: 1,
        minWidth: 120
      },
      {
        field: "customer.name",
        headerName: t("entity:order:customer"),
        valueGetter: (_, row) => row.customer?.name || "-",
        minWidth: 160,
        flex: 0.5,
      },
      {
        field: "mission.actors.requester.name",
        headerName: t("common:tables:requester"),
        valueGetter: (_, row) => row.mission?.actors.ownerIsNotRequester ? row.mission?.actors.requester.name : row.mission?.actors.owner.name,
        flex: 1,
        minWidth: 120,
      },
      {
        field: "mission.target.carDetails.registration",
        headerName: t("entity:mission:target:registration"),
        valueGetter: (_, row) => {
          const type = row.mission?.target.type;
          const target = type === 1 ? row.mission?.target?.carDetails : (type === 2 ? row.mission?.target?.boatDetails : null);
          return target?.registration || "-";
        },
        minWidth: 100,
        flex: 0.5
      },
      {
        field: "payment.calculatedTotal.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:payment:calculatedTotal"),
        valueGetter: (_, row) => row.payment.calculatedTotal?.value || "0",
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.value)} ${getCurrencyShorthand(t, params.row.payment.calculatedTotal?.currency)}`,
        minWidth: 120
      },
      {
        field: "deductible.price.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:deductible"),
        valueGetter: (_, row) => Number(row.deductible?.price?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => params.row.deductible ? `${FixNumber(params.row.deductible?.price?.value)} ${getCurrencyShorthand(t, params.row.deductible?.price?.currency)}` : "-",
        minWidth: 120
      },
      {
        field: "mission.actors.contract.agreement.name",
        headerName: t("entity:agreement:type"),
        valueGetter: (_, row) => row.mission?.actors?.contract?.agreement?.name || "-",
        minWidth: 160,
        flex: 0.5
      },
      {
        field: "comment",
        headerName: t("entity:order:internalComment"),
        valueGetter: (_, row) => row.comment || "-",
        minWidth: 160,
        flex: 0.5
      },
    ]
  }, [t]);

  const onRowClick = useCallback((params, ev) => {
    navigate(!tabId ? `./ready/${params.row.missionId}/${params.id}` : `./${params.row.missionId}/${params.id}`, { relative: "path" });
  }, [navigate, tabId]);

  const onExportAll = useCallback(async () => {
    const { success, exportId, openResultPage } = await exportAll(selectionModel);
    if (!!success) {
      setSelectionModel([]);
      if (!!openResultPage) {
        navigate(!tabId ? `./journals/${exportId}` : `../journals/${exportId}`, { relative: "path" });
      }
    }
  }, [exportAll, navigate, tabId, selectionModel]);

  return (
    <>
      {
        orders.length !== 0 ? (
          <ToggleSurface surface xl={4} lg={5} md={6} sm={8} xs={12}>
            <Grid item container xs={12} spacing={1}>

              <Grid item xs={12}>
                <Typography variant="subtitle1">{t("readyForInvoicing:exportAllTitle")}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2">
                  {t("readyForInvoicing:exportAllDescriptionPart1")} {selectionModel.length} {t("readyForInvoicing:exportAllDescriptionPart2")}
                </Typography>
              </Grid>

              {
                !hasExportSettings ? (
                  <MissingExportSettingsWarning />
                ) : null
              }

              <Grid item xs={12} container justifyContent="flex-end">
                <Button variant="contained" disabled={showLoading || !hasExportSettings || selectionModel.length === 0} onClick={onExportAll}>{t("readyForInvoicing:exportAllButton")}</Button>
              </Grid>
            </Grid>
          </ToggleSurface>
        ) : null
      }

      <ToggleSurface>
        <Grid item xs={12}>
          <SimpleTable
            tableId="readyforinvoicing"
            onRowClick={onRowClick}
            columns={columns}
            rows={orders}
            loading={showLoading}
            emptyStateTitle={t("readyForInvoicing:emptyStateTitle")}
            emptyStateDescription={t("readyForInvoicing:emptyStateDescription")}
            checkboxSelection
            disablePaging
            onSelectionModelChange={setSelectionModel}
            selectionModel={selectionModel}
            xs={12}
          />
        </Grid>
      </ToggleSurface>

      {
        !!dialogAction ? (
          <InvoiceExportDialog
            exportAction={exportAction}
            dialogAction={dialogAction}
            exportResult={exportResult}
            working={working}
          />
        ) : null
      }
    </>
  );
}

